import { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import api, { fileUrl } from "@/lib/api";
import { PageHero, Reveal, EmptyState } from "@/components/public/Shared";

export default function Gallery() {
  const [photos, setPhotos] = useState(null);
  const [category, setCategory] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/public/gallery").then((r) => setPhotos(r.data)).catch(() => setPhotos([]));
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set((photos || []).map((p) => p.category).filter(Boolean))];
    return ["All", ...cats];
  }, [photos]);

  const filtered = useMemo(
    () => (photos || []).filter((p) => category === "All" || p.category === category),
    [photos, category]
  );

  const openIdx = lightbox ? filtered.findIndex((p) => p.id === lightbox.id) : -1;

  return (
    <div data-testid="gallery-page">
      <PageHero title="Photo Gallery" subtitle="Moments from the life of our church family." />
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
        {photos && photos.length > 0 && (
          <div className="mb-10 flex flex-wrap gap-2" data-testid="gallery-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                  category === c ? "bg-navy text-cream" : "bg-white text-navy/70 border border-border hover:bg-secondary"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {photos && photos.length === 0 && (
          <EmptyState testId="gallery-empty" title="Photos coming soon" text="Photographs from our church services and events will be published here." />
        )}
        {photos && photos.length > 0 && filtered.length === 0 && (
          <EmptyState testId="gallery-category-empty" title="No photos in this category" text="Please choose another category." />
        )}

        <div className="columns-2 gap-4 md:columns-3 [&>figure]:mb-4">
          {filtered.map((p, i) => (
            <Reveal key={p.id} delay={Math.min(i * 0.04, 0.3)}>
              <figure
                className="group cursor-pointer overflow-hidden rounded-lg border border-border bg-white"
                onClick={() => setLightbox(p)}
                data-testid={`gallery-photo-${p.id}`}
              >
                <img
                  src={fileUrl(p.image_url)}
                  alt={p.title || "Church photograph"}
                  className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {(p.title || p.caption) && (
                  <figcaption className="p-4">
                    {p.title && <p className="font-heading text-sm font-bold text-navy">{p.title}</p>}
                    {p.caption && <p className="mt-1 text-xs text-muted-foreground">{p.caption}</p>}
                  </figcaption>
                )}
              </figure>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent className="max-w-4xl border-none bg-navy/95 p-2 sm:p-4" data-testid="gallery-lightbox">
          {lightbox && (
            <div className="relative">
              <img src={fileUrl(lightbox.image_url)} alt={lightbox.title || "Church photograph"} className="max-h-[75vh] w-full rounded-md object-contain" />
              <div className="mt-3 flex items-center justify-between gap-3 px-1">
                <div className="min-w-0">
                  {lightbox.title && <p className="font-heading text-lg font-bold text-cream truncate">{lightbox.title}</p>}
                  {lightbox.caption && <p className="text-xs text-cream/70">{lightbox.caption}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    aria-label="Previous photo"
                    data-testid="lightbox-prev"
                    className="rounded-full bg-cream/10 p-2 text-cream hover:bg-cream/20 transition-colors"
                    onClick={() => openIdx > 0 && setLightbox(filtered[openIdx - 1])}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    aria-label="Next photo"
                    data-testid="lightbox-next"
                    className="rounded-full bg-cream/10 p-2 text-cream hover:bg-cream/20 transition-colors"
                    onClick={() => openIdx < filtered.length - 1 && setLightbox(filtered[openIdx + 1])}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <button
                    aria-label="Close"
                    data-testid="lightbox-close"
                    className="rounded-full bg-cream/10 p-2 text-cream hover:bg-cream/20 transition-colors"
                    onClick={() => setLightbox(null)}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
